import { Flame, Trophy, Clock, Activity, Award, Timer } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { MyProgressResponse, ProgressPeriod } from '@/lib/api-client';
import { Card, Skeleton, StatTile } from '@ai-tutor/ui';

interface StreaksSectionProps {
  data: MyProgressResponse | undefined;
  isLoading: boolean;
  period: ProgressPeriod;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Label every Nth cell so the 52-week year row doesn't crowd. */
const YEAR_LABEL_INTERVAL = 4;

// Mirrors the legacy `INTENSITY_CLASSES` palette so the heatmap reads as
// part of the same design system. Buckets are tuned for weekly cells -
// each step is roughly an additional hour of study.
const INTENSITY_CLASSES = [
  // `bg-secondary`, not `bg-slate-100` — the other three steps are already on
  // the brand ramp, so the empty bucket was the one raw-palette value in a
  // token ramp. The `dark:` override stays: `--secondary` sits too close to
  // `--card` in dark to read as an empty cell, the same reason `Skeleton`
  // carries `dark:bg-white/10`.
  'bg-secondary dark:bg-white/[0.06]',
  'bg-brand-200 dark:bg-primary/40',
  'bg-brand-400 dark:bg-primary/70',
  'bg-brand-600 dark:bg-primary',
];

function intensityBucket(hours: number): number {
  if (hours === 0) return 0;
  if (hours < 0.75) return 1;
  if (hours < 2) return 2;
  return 3;
}

/**
 * Loading state for this section: the heatmap card plus the four metric cards.
 *
 * Mirrors the loaded markup class-for-class on the containers - same card
 * chrome, same `mt-3 grid grid-cols-2 ... lg:grid-cols-4` - so the section
 * occupies its final footprint before the data arrives.
 *
 * The cell strip is drawn at the WEEKLY count (13) rather than the period's
 * real bucket count, because the count comes from `data.activity_heatmap`,
 * which by definition has not loaded yet. A fixed strip is the honest choice;
 * guessing 7 or 52 from the period would be right a third of the time.
 */
function StreaksSkeleton() {
  return (
    <>
      <Card elevation='none' className='px-5 py-4'>
        <div className='mb-3 flex flex-wrap items-center gap-x-5 gap-y-2'>
          <div className='inline-flex items-baseline gap-1.5'>
            <div className='h-4 w-4 self-center rounded-sm bg-secondary' />
            <Skeleton className='h-5 w-8' />
            <Skeleton className='h-3 w-16' />
          </div>
          <div className='h-5 w-px bg-secondary' />
          <div className='inline-flex items-baseline gap-1.5'>
            <div className='h-4 w-4 self-center rounded-sm bg-secondary' />
            <Skeleton className='h-5 w-8' />
            <Skeleton className='h-3 w-8' />
          </div>
          <Skeleton className='ml-auto h-3 w-24' />
        </div>
        <div className='flex items-end gap-1'>
          {Array.from({ length: 13 }).map((_, i) => (
            <Skeleton
              key={i}
              className='h-5 flex-1 rounded-[3px]'
              // A touch of stagger so the strip reads as one sweeping element
              // rather than 13 blocks blinking in lockstep.
              style={{ animationDelay: `${i * 25}ms` }}
            />
          ))}
        </div>
      </Card>

      <div className='mt-3 grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4'>
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} elevation='none' className='px-4 py-3'>
            <div className='mb-1.5 flex items-center gap-1.5'>
              <Skeleton className='h-3.5 w-3.5 rounded-sm' />
              <Skeleton className='h-3 w-20' />
            </div>
            <Skeleton className='h-6 w-20' />
          </Card>
        ))}
      </div>
    </>
  );
}

export function StreaksSection({
  data,
  isLoading,
  period,
}: StreaksSectionProps) {
  if (isLoading) return <StreaksSkeleton />;
  if (!data) return null;

  const activeBuckets = data.activity_heatmap.filter(c => c.hours > 0).length;
  const totalBuckets = data.activity_heatmap.length;
  // Granularity inferred from period - daily for week, weekly for month/year.
  const isDailyHeatmap = period === 'this_week';
  const bucketUnit = isDailyHeatmap ? 'days' : 'weeks';
  const cellHeight = isDailyHeatmap ? 'h-7' : 'h-5';
  // A phone has ~320px for the strip, and 52 cells at 6px plus their gaps is
  // over 500, so below `sm` the year strip drops the floor (and `Heatmap`
  // narrows the gap): the cells share the width, about 4px each.
  const cellMinWidth =
    totalBuckets > 30
      ? 'min-w-0 sm:min-w-[6px]'
      : totalBuckets > 8
        ? 'min-w-[14px]'
        : 'min-w-[28px]';
  const cellClass = `${cellHeight} ${cellMinWidth} flex-1`;

  // Sub-area emptiness flags - each one independent so the rest of the
  // card keeps rendering even when one signal is missing.
  const streakEmpty = data.streak.current === 0 && data.streak.longest === 0;
  const heatmapEmpty = totalBuckets > 0 && activeBuckets === 0;

  // Avg session reads in minutes for short sittings, but rolls up to hours once
  // it crosses 60 — a bare "445 min" is unreadable (and a hint the session
  // duration isn't idle-capped, which is a separate backend concern).
  const avgMin = data.avg_session_minutes;
  const avgSession =
    avgMin >= 60
      ? { value: Math.round((avgMin / 60) * 10) / 10, unit: 'hrs' }
      : { value: Math.round(avgMin), unit: 'min' };

  return (
    <>
      {/* Activity heatmap card - streak chips live here (always-on,
          period-independent), with the active-buckets counter aligned right. */}
      <Card elevation='none' className='px-5 py-4'>
        <div className='mb-3 flex flex-wrap items-center gap-x-5 gap-y-2'>
          {streakEmpty ? (
            <span className='text-sm text-muted-foreground'>
              No streak yet - a 10-minute meaningful session starts one.
            </span>
          ) : (
            <>
              <span className='inline-flex items-baseline gap-1.5'>
                <Flame className='h-4 w-4 self-center text-brand-400 dark:text-primary' />
                <span className='text-lg font-bold tabular-nums text-foreground'>
                  {data.streak.current}
                </span>
                <span className='text-xs text-muted-foreground'>
                  day streak
                </span>
              </span>
              <div className='h-5 w-px bg-secondary' />
              <span className='inline-flex items-baseline gap-1.5'>
                <Trophy className='h-4 w-4 self-center text-violet-400' />
                <span className='text-lg font-semibold tabular-nums text-foreground'>
                  {data.streak.longest}
                </span>
                <span className='text-xs text-muted-foreground'>best</span>
              </span>
            </>
          )}
          {totalBuckets > 0 && (
            <span className='ml-auto text-xs text-muted-foreground'>
              <span className='font-semibold tabular-nums text-foreground'>
                {activeBuckets}
              </span>
              <span className='text-muted-foreground'>/{totalBuckets}</span>{' '}
              active {bucketUnit}
            </span>
          )}
        </div>
        {totalBuckets > 0 && (
          <>
            <Heatmap
              cells={data.activity_heatmap}
              cellClass={cellClass}
              period={period}
            />
            {heatmapEmpty && (
              <p className='mt-2 text-center text-xs text-muted-foreground'>
                No activity in this period.
              </p>
            )}
          </>
        )}
      </Card>

      {/* Aggregate metrics grid */}
      <div className='mt-3 grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4'>
        <StatTile
          icon={Clock}
          label='Study time'
          value={data.study_time_hours}
          unit='hrs'
        />
        <StatTile icon={Activity} label='Sessions' value={data.sessions} />
        <StatTile
          icon={Timer}
          label='Avg session'
          value={avgSession.value}
          unit={avgSession.unit}
        />
        <StatTile
          icon={Award}
          label='Concepts mastered'
          value={data.concepts_mastered}
        />
      </div>
    </>
  );
}

interface HeatmapProps {
  cells: { iso_week_start: string; hours: number }[];
  cellClass: string;
  period: ProgressPeriod;
}

function Heatmap({ cells, cellClass, period }: HeatmapProps) {
  const labels = computeLabels(cells, period);
  const isDaily = period === 'this_week';
  const gap = cells.length > 30 ? 'gap-0.5 sm:gap-1' : 'gap-1';

  return (
    <div className='space-y-1.5'>
      <div className={`flex items-end ${gap}`}>
        {cells.map((cell, i) => {
          // "You are here" ring on the most recent bucket - but only when
          // that bucket actually has activity. Highlighting an empty cell
          // reads as a floating outline with nothing behind it.
          const isLast = i === cells.length - 1;
          const showNowRing = isLast && cell.hours > 0;
          return (
            <div
              key={cell.iso_week_start + i}
              title={tooltip(cell, isDaily)}
              className={`${cellClass} rounded-[3px] transition-colors ${
                INTENSITY_CLASSES[intensityBucket(cell.hours)]
              } ${showNowRing ? 'ring-1 ring-brand-400 ring-offset-1 dark:ring-primary dark:ring-offset-neutral-800' : ''}`}
            />
          );
        })}
      </div>

      {/* Bucket labels - daily uses Mon..Sun; month/year use week numbers. */}
      {/* A label is centred on its cell and may be wider than it (a 4px
          year cell under "W13"): `justify-center` on a flex box overflows
          both sides evenly, where `text-center` would spill to the right. */}
      <div className={`flex ${gap}`}>
        {labels.map((label, i) => (
          <div
            key={`label-${cells[i]?.iso_week_start}-${i}`}
            className={`${cellClass} flex justify-center whitespace-nowrap text-xs text-muted-foreground ${
              label && !phoneLabel(i, cells.length) ? 'max-sm:invisible' : ''
            }`}
          >
            {label ?? ''}
          </div>
        ))}
      </div>
    </div>
  );
}

function tooltip(
  cell: { iso_week_start: string; hours: number },
  isDaily: boolean
): string {
  const d = new Date(cell.iso_week_start);
  const formatted = d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  const prefix = isDaily ? '' : 'Week of ';
  return `${prefix}${formatted}: ${cell.hours}h`;
}

/**
 * Whether a year-strip label still shows on a phone, where the strip is about
 * 320px: every 8th week rather than every 4th, plus the last, with the one
 * before the last dropped when it would sit on top of it. Short strips (week,
 * month) keep every label.
 */
function phoneLabel(i: number, count: number): boolean {
  if (count <= 30 || i === count - 1) return true;
  const interval = YEAR_LABEL_INTERVAL * 2;
  return i % interval === 0 && count - 1 - i >= interval / 2;
}

/**
 * Build the label row under the heatmap, varying by period:
 *  - This Week  → Mon..Sun
 *  - This Month → W1..W5 (week of month)
 *  - This Year  → W1..W52, but sparse (every Nth) so labels don't crowd
 */
function computeLabels(
  cells: { iso_week_start: string }[],
  period: ProgressPeriod
): Array<string | null> {
  if (period === 'this_week') {
    return cells.map((_, i) => DAY_LABELS[i] ?? null);
  }
  if (period === 'this_month') {
    return cells.map((_, i) => `W${i + 1}`);
  }
  // this_year - show first, last, and every Nth in between.
  return cells.map((_, i) => {
    if (i === 0 || i === cells.length - 1) return `W${i + 1}`;
    return i % YEAR_LABEL_INTERVAL === 0 ? `W${i + 1}` : null;
  });
}
